import { Button } from '@components/Button';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { ErrorBoundary as DatadogErrorBoundary } from '@datadog/browser-rum-nextjs';
import { type ReactNode, useRef, useState } from 'react';

/**
 * Our standard Error Boundary Fallback component
 * @param {error, resetErrorBoundary}
 * @returns JSX element
 */
function ErrorBoundaryFallback({
  error,
  resetErrorBoundary,
  location,
}: {
  error: Error;
  resetErrorBoundary: () => void;
  location: string;
}) {
  const err = error instanceof Error ? error : new Error(String(error));
  return (
    <div role="alert">
      <div className="mx-auto my-0.5 max-w-max-width border border-solid border-grey-light p-2">
        <Paragraph>
          Uh oh, we had a little problem displaying this section.
        </Paragraph>
        <Paragraph>
          Our Web Team has been notified and will be looking into it shortly.
        </Paragraph>
        <Paragraph>Error code:</Paragraph>
        <pre className="rounded bg-grey-light p-2.5 dark:bg-grey-dark">
          {err.message}
          <br />
          {err.name}
          <br />
          Thrown by: {location}
        </pre>
        <Paragraph>
          If this error persists, please get in contact with our Customer
          Service Team for help.
        </Paragraph>
        <Button onClick={resetErrorBoundary}>Try again</Button>
      </div>
    </div>
  );
}

function Bomb({ username }) {
  if (username === 'bomb') {
    throw new Error('💥 KABOOM 💥');
  }
  return <>Hi {username}</>;
}

export function ErrorTest() {
  const [username, setUsername] = useState('');
  const usernameRef = useRef(null);

  return (
    <div>
      <label>
        {`Username (don't type "bomb"): `}
        <input
          placeholder={`type "bomb"`}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          ref={usernameRef}
        />
      </label>
      <div>
        <Bomb username={username} />
      </div>
    </div>
  );
}

export function BulkErrorBoundary({
  children,
  location,
}: {
  children: ReactNode;
  location: string;
}) {
  return (
    <DatadogErrorBoundary
      fallback={({
        error,
        resetError,
      }: {
        error: Error;
        resetError: () => void;
      }) => (
        <ErrorBoundaryFallback
          error={error}
          resetErrorBoundary={resetError}
          location={location}
        />
      )}
    >
      {children}
    </DatadogErrorBoundary>
  );
}
